import React, { useState } from 'react';
import {
  Bell,
  Check,
  X,
  Clock,
  ChefHat,
  AlertCircle,
  ShoppingBag,
  User,
  Phone,
  MessageSquare,
  Loader2,
  Printer,
  RefreshCw,
  Calendar,
  Send,
} from 'lucide-react';
import useQROrders from '../../hooks/useQROrders';
import useBranch from '../../hooks/useBranch';
import QRKOTPreview from './QRKOTPreview';
import { useAuth } from '../../context/AuthContext';
import { P } from '../../config/permissionCodes';

const STATUS_FILTERS = [
  { id: '', label: 'All' },
  { id: 'PENDING', label: 'Pending' },
  { id: 'APPROVED', label: 'Approved' },
  { id: 'REJECTED', label: 'Rejected' },
];

const statusConfig = {
  PENDING: { color: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  APPROVED: { color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: Check },
  REJECTED: { color: 'bg-red-50 text-red-700 border-red-200', icon: X },
  CANCELLED: { color: 'bg-slate-100 text-slate-500 border-slate-200', icon: AlertCircle },
  EXPIRED: { color: 'bg-slate-100 text-slate-400 border-slate-200', icon: Clock },
};

function OrderCard({ order, onSendKOT, onReject, isPending, canSendKitchen }) {
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [sending, setSending] = useState(false);
  const [submittingReject, setSubmittingReject] = useState(false);

  const cfg = statusConfig[order.status] || statusConfig.PENDING;
  const StatusIcon = cfg.icon;
  const isProcessing = sending;

  const handleSendKOT = async () => {
    setSending(true);
    try {
      await onSendKOT(order.id);
    } finally {
      setSending(false);
    }
  };

  const handleReject = async () => {
    setSubmittingReject(true);
    try {
      await onReject(order.id, rejectReason || 'Rejected by staff');
      setRejecting(false);
      setRejectReason('');
    } finally {
      setSubmittingReject(false);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m ago`;
  };

  return (
    <div className={`bg-white rounded-xl border ${isPending ? 'border-amber-200 shadow-sm' : 'border-slate-200'} overflow-hidden`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-center justify-between ${isPending ? 'bg-amber-50/50' : 'bg-slate-50/50'} border-b border-slate-100`}>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <ShoppingBag size={14} className="text-slate-400" />
            <span className="text-xs font-bold text-slate-900">#{order.id}</span>
          </div>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.color}`}>
            <StatusIcon size={10} />
            {order.status}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-400">
          <span>{order.tableName}</span>
          <span>{timeAgo(order.createdAt)}</span>
        </div>
      </div>

      {/* Customer Info */}
      {(order.customerName || order.customerPhone) && (
        <div className="px-4 py-2 flex items-center gap-4 border-b border-slate-50 bg-slate-50/30">
          {order.customerName && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <User size={11} /> {order.customerName}
            </span>
          )}
          {order.customerPhone && (
            <span className="flex items-center gap-1 text-xs text-slate-500">
              <Phone size={11} /> {order.customerPhone}
            </span>
          )}
        </div>
      )}

      {/* Items */}
      <div className="px-4 py-3">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
              <th className="text-left pb-2">Item</th>
              <th className="text-center pb-2 w-12">Qty</th>
              <th className="text-right pb-2 w-20">Price</th>
            </tr>
          </thead>
          <tbody>
            {(order.items || []).map((item, i) => (
              <tr key={i} className="border-t border-slate-50">
                <td className="py-1.5">
                  <span className="text-xs font-medium text-slate-800">{item.itemName}</span>
                  {item.variantName && (
                    <span className="text-[10px] text-slate-400 ml-1">({item.variantName})</span>
                  )}
                  {item.specialInstructions && (
                    <p className="text-[10px] text-amber-600 mt-0.5 flex items-center gap-1">
                      <MessageSquare size={9} /> {item.specialInstructions}
                    </p>
                  )}
                </td>
                <td className="text-center text-xs text-slate-600">{item.quantity}</td>
                <td className="text-right text-xs text-slate-600">{Number(item.totalPrice).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Notes */}
        {order.notes && (
          <div className="mt-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
            <p className="text-[10px] font-medium text-amber-700">Note: {order.notes}</p>
          </div>
        )}

        {/* Rejection Reason */}
        {order.rejectionReason && (
          <div className="mt-2 p-2 bg-red-50 rounded-lg border border-red-100">
            <p className="text-[10px] font-medium text-red-700">Rejected: {order.rejectionReason}</p>
          </div>
        )}

        {/* Total */}
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
          <span className="text-xs text-slate-500">{order.totalItems} item{order.totalItems !== 1 ? 's' : ''}</span>
          <span className="text-sm font-bold text-slate-900">{Number(order.totalAmount).toFixed(2)}</span>
        </div>
      </div>

      {/* Actions (Pending only) */}
      {isPending && (
        <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/30">
          {rejecting ? (
            <div className="space-y-2">
              <input
                type="text"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (optional)"
                className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-50"
                autoFocus
              />
              <div className="flex items-center gap-2">
                <button
                  onClick={handleReject}
                  disabled={submittingReject}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {submittingReject ? <Loader2 size={12} className="animate-spin" /> : <X size={12} />}
                  Confirm Reject
                </button>
                <button
                  onClick={() => { setRejecting(false); setRejectReason(''); }}
                  className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <button
                  onClick={handleSendKOT}
                  disabled={isProcessing || !canSendKitchen}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg ${canSendKitchen ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-slate-100 text-slate-400 cursor-not-allowed'} disabled:opacity-50 transition-colors`}
                  title={!canSendKitchen ? 'You do not have permission to send KOT' : ''}
                >
                  {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                  Send KOT
                </button>
              </div>
              <button
                onClick={() => setRejecting(true)}
                disabled={isProcessing}
                className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] font-medium rounded-lg border border-red-200 bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors"
              >
                <X size={11} />
                Reject
              </button>
            </div>
          )}
        </div>
      )}

      {/* Approved info */}
      {order.status === 'APPROVED' && order.approvedBy && (
        <div className="px-4 py-2 border-t border-slate-100 bg-emerald-50/30">
          <p className="text-[10px] text-emerald-600">
            Approved by {order.approvedBy} {order.orderId ? `| Order #${order.orderId}` : ''}
          </p>
        </div>
      )}
    </div>
  );
}



export default function QROrdersPanel() {
  const { hasPermission } = useAuth();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const {
    pendingOrders,
    allOrders,
    loading,
    statusFilter,
    setStatusFilter,
    dateFilter,
    setDateFilter,
    approveOrder,
    rejectOrder,
    refreshPending,
    refreshAll,
  } = useQROrders({ autoRefresh });

  const { branch } = useBranch();
  const kotModes = branch?.kotModes || ['PRINT'];
  // Permission checks for QR_MENU incoming orders
  // Button should only be enabled if BOTH permissions are present
  const canSendKitchen = hasPermission(P.POS.DINE_IN.INCOMING.SEND_KITCHEN) && hasPermission(P.POS.DINE_IN.INCOMING.PRINT_KOT);
  const canPrintKOT    = hasPermission(P.POS.DINE_IN.INCOMING.PRINT_KOT);
  const shouldPrintKOT = kotModes.includes('PRINT') && canPrintKOT;

  const [tab, setTab] = useState('pending');
  const [kotPreviewOrder, setKotPreviewOrder] = useState(null);

  // Unified handler: approve order, then print KOT if branch config requires it
  const handleSendKOT = async (id) => {
    if (!canSendKitchen) {
      // Optionally show a toast or error
      return;
    }
    const result = await approveOrder(id);
    if (result && shouldPrintKOT) {
      setKotPreviewOrder(result);
    }
  };

  // Manual refresh
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshPending();
      if (tab === 'history') await refreshAll();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-amber-100 rounded-lg">
          <ChefHat size={20} className="text-amber-600" />
        </div>
        <div>
          <h1 className="text-lg font-semibold text-slate-900">QR Orders</h1>
          <p className="text-xs text-slate-500">Manage incoming orders from QR code scans</p>
        </div>

        {/* Auto-refresh toggle + Refresh button */}
        <div className="ml-auto flex items-center gap-3">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div className="relative">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-8 h-[18px] bg-slate-300 rounded-full peer-checked:bg-teal-500 transition-colors" />
              <div className="absolute top-[2px] left-[2px] w-[14px] h-[14px] bg-white rounded-full shadow-sm peer-checked:translate-x-[14px] transition-transform" />
            </div>
            <span className="text-[11px] font-medium text-slate-500">Auto-refresh</span>
          </label>
          {autoRefresh && (
            <span className="flex items-center gap-1.5 text-[10px] text-teal-500">
              <span className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse" />
              Every 5s
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </button>

          {pendingOrders.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 border border-amber-200 rounded-full animate-pulse">
              <Bell size={14} className="text-amber-600" />
              <span className="text-xs font-bold text-amber-700">{pendingOrders.length} pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-lg p-0.5 w-fit">
        <button
          onClick={() => setTab('pending')}
          className={`relative px-4 py-2 text-xs font-medium rounded-md transition-colors ${
            tab === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Pending
          {pendingOrders.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {pendingOrders.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('history')}
          className={`px-4 py-2 text-xs font-medium rounded-md transition-colors ${
            tab === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          History
        </button>
      </div>

      {/* Pending Tab */}
      {tab === 'pending' && (
        <div>
          {pendingOrders.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <div className="mx-auto w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4">
                <Check size={24} className="text-emerald-500" />
              </div>
              <h3 className="text-sm font-semibold text-slate-900 mb-1">No pending orders</h3>
              <p className="text-xs text-slate-500">
                {autoRefresh ? 'New QR orders will appear here automatically' : 'Click Refresh to check for new orders'}
              </p>
              {autoRefresh && (
                <p className="text-[10px] text-slate-400 mt-2">Polling every 5 seconds...</p>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {pendingOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onSendKOT={handleSendKOT}
                  onReject={rejectOrder}
                  isPending
                  canSendKitchen={canSendKitchen}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div>
          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setStatusFilter(f.id)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    statusFilter === f.id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar size={13} className="text-slate-400" />
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-2.5 py-1.5 text-xs font-medium border border-slate-200 rounded-lg bg-white focus:outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-50 text-slate-700"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-slate-400" />
            </div>
          ) : allOrders.length === 0 ? (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <p className="text-xs text-slate-500">No QR orders found</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {allOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onSendKOT={handleSendKOT}
                  onReject={rejectOrder}
                  isPending={false}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* KOT Print Preview Modal */}
      {kotPreviewOrder && (
        <QRKOTPreview
          order={kotPreviewOrder}
          onClose={() => setKotPreviewOrder(null)}
        />
      )}
    </div>
  );
}
